<template>
    <!-- Conteneur principal pleine largeur -->
    <div class="ppv2-w-full ppv2-relative">
        <!-- Loading Overlay - Couvre toute la page -->
        <div
            v-if="loading"
            class="ppv2-fixed ppv2-inset-0 ppv2-z-50 ppv2-bg-opacity-90 ppv2-flex ppv2-items-center ppv2-justify-center"
        >
            <div class="ppv2-text-center">
                <div
                    class="ppv2-animate-spin ppv2-rounded-full ppv2-h-16 ppv2-w-16 ppv2-border-b-4 ppv2-border-purple-600 ppv2-mx-auto"
                ></div>
                <span class="ppv2-mt-4 ppv2-block ppv2-text-lg poppinsMedium ppv2-text-gray-700">{{
                    $t('invoices.loading')
                }}</span>
            </div>
        </div>
        <div v-else>
            <div class="ppv2-rounded-lg ppv2-shadow-sm ppv2-p-4 md:ppv2-p-6">
                <!-- Message d'erreur -->
                <div v-if="error" class="ppv2-bg-red-50 ppv2-border ppv2-border-red-200 ppv2-rounded-lg ppv2-p-4 ppv2-mb-4">
                    <div class="ppv2-flex ppv2-items-center">
                        <svg class="ppv2-h-5 ppv2-w-5 ppv2-text-red-600 ppv2-mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <span class="ppv2-text-red-800">{{ error }}</span>
                    </div>
                </div>

                <!-- Header -->
                <Header :title="$t('invoices.title')" />

                <!-- Stats Cards (Composant dynamique) -->
                <invoice-stats-cards
                    ref="statsCards"
                    :start-date="dateRange.start"
                    :end-date="dateRange.end"
                ></invoice-stats-cards>

                <!-- Filters Row -->
                <div class="ppv2-mb-6 ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-3">
                    <!-- Date Range Picker (Composant dynamique) -->
                    <invoice-date-filter
                        :start-date.sync="dateRange.start"
                        :end-date.sync="dateRange.end"
                        @change="handleDateChange"
                    >
                    </invoice-date-filter>

                    <!-- More Filters -->

                    <button
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-transition-colors"
                    >
                        <FileText :size="17" />
                        {{ $t('invoices.filter_type') }}
                    </button>
                    <button
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-transition-colors"
                    >
                        <CalendarClock :size="17" />
                        {{ $t('invoices.filter_due_date') }}
                    </button>
                    <button
                        @click="showAdvancedFilters = true"
                        class="ppv2-flex ppv2-items-center ppv2-gap-2 ppv2-border ppv2-border-solid ppv2-border-black ppv2-rounded-lg ppv2-px-3 ppv2-py-2 ppv2-text-sm ppv2-text-gray-700 hover:ppv2-bg-gray-50 ppv2-transition-colors"
                    >
                        <LayoutGrid :size="17" />
                        {{ $t('invoices.filter_advanced') }}
                    </button>
                </div>

                <!-- Active Filters Pills -->
                <div v-if="activeFiltersCount > 0" class="ppv2-mb-4 ppv2-flex ppv2-flex-wrap ppv2-gap-2 ppv2-items-center">
                    <span
                        v-for="(filter, key) in activeFiltersPills"
                        :key="key"
                        class="ppv2-inline-flex ppv2-items-center ppv2-gap-2 ppv2-bg-blue-100 ppv2-text-blue-800 ppv2-px-3 ppv2-py-1.5 ppv2-rounded-full ppv2-text-sm poppinsMedium"
                    >
                        <span>{{ filter.label }}: {{ filter.value }}</span>
                        <button
                            @click="removeFilter(key)"
                            class="ppv2-text-blue-600 hover:ppv2-text-blue-900 poppinsBold ppv2-text-lg"
                        >
                            ×
                        </button>
                    </span>
                    <button
                        @click="clearAllFilters"
                        class="ppv2-text-sm ppv2-text-red-600 hover:ppv2-text-red-800 ppv2-underline poppinsMedium"
                    >
                        {{ $t('invoices.remove_all_filters') }}
                    </button>
                </div>

                <!-- Filter Pills (Status filters) -->
                <div class="ppv2-mb-6 ppv2-flex ppv2-flex-wrap ppv2-items-center ppv2-gap-3">
                    <button
                        v-for="filter in filters"
                        :key="filter.id"
                        :class="[
                            'ppv2-rounded-full md:ppv2-px-4 ppv2-px-2 ppv2-py-1.5 ppv2-text-sm poppinsMedium ppv2-transition-colors',
                            activeFilter === filter.id
                                ? 'ppv2-bg-gray-900 ppv2-text-white'
                                : 'ppv2-bg-gray-100 ppv2-text-gray-700 hover:ppv2-bg-gray-200',
                        ]"
                        @click="activeFilter = filter.id"
                    >
                        {{ filter.label }} <span class="ppv2-ml-1">{{ filter.count }}</span>
                    </button>

                    <div class="ppv2-ml-auto ppv2-flex ppv2-items-center ppv2-gap-3">
                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search
                                v-model="searchQuery"
                                :emitGlobal="false"
                                :keep-open-on-search="true"
                                @search-updated="handleSearchUpdate"
                            />
                        </div>

                        <!-- Export Button -->
                        <ExportSelect label="Export" :options="exportOptions" @export-selected="handleExport" />

                        <!-- Column Visibility Toggle -->
                        <div class="ppv2-relative" ref="columnsDropdown">
                            <button
                                @click="showColumnSelector = !showColumnSelector"
                                class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                            >
                                <FilePen :size="22" />
                                <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(15) }">{{
                                    $t('invoices.edit_columns')
                                }}</span>
                                <ChevronDown :size="16" />
                            </button>
                            <!-- Dropdown -->
                            <div
                                v-if="showColumnSelector && referenceData"
                                class="menuDropdown ppv2-absolute ppv2-top-full ppv2-right-0 ppv2-rounded-md ppv2-overflow-hidden ppv2-min-w-[250px]"
                            >
                                <div class="ppv2-max-h-[400px] ppv2-overflow-y-auto">
                                    <label
                                        v-for="column in referenceData.availableColumns"
                                        :key="column.id"
                                        class="dropdownItem ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-cursor-pointer ppv2-transition-colors"
                                    >
                                        <BaseCheckbox
                                            :value="isColumnSelected(column.id)"
                                            @input="toggleColumn(column)"
                                            size="sm"
                                            :hasBorderChecked="true"
                                        />
                                        <span class="poppinsLight" :style="{ fontSize: normalizeFontSize(14) }">{{
                                            column.title
                                        }}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- DataTable (Composant dynamique) -->
                <invoice-data-table
                    ref="invoiceDataTable"
                    :start-date="dateRange.start"
                    :end-date="dateRange.end"
                    :selected-columns="referenceData ? referenceData.userFilters.selected_columns : []"
                    :filters="referenceData && referenceData.userFilters ? referenceData.userFilters.filters : {}"
                    :payment-status="activeFilter"
                    @search="handleSearch"
                >
                </invoice-data-table>

                <!-- Modal Filtres Avancés -->
                <invoice-advanced-filters
                    v-if="showAdvancedFilters && referenceData"
                    :available-filters="referenceData"
                    :current-filters="
                        Array.isArray(referenceData.userFilters.filters) ? {} : referenceData.userFilters.filters || {}
                    "
                    @update="updateFilters"
                    @close="showAdvancedFilters = false"
                />

                <!-- Dialogue de confirmation pour retirer tous les filtres -->
                <confirmation-dialog
                    :is-open="showClearFiltersDialog"
                    type="warning"
                    :title="$t('invoices.clear_filters_dialog.title')"
                    :message="$t('invoices.clear_filters_dialog.message')"
                    :confirm-button-text="$t('invoices.clear_filters_dialog.confirm')"
                    :cancel-button-text="$t('invoices.clear_filters_dialog.cancel')"
                    @confirm="confirmClearAllFilters"
                    @cancel="cancelClearFilters"
                />

                <!-- Table -->
                <div class="ppv2-overflow-x-auto">
                    <table class="ppv2-w-full ppv2-min-w-[800px]">
                        <thead>
                            <tr class="ppv2-border-b ppv2-border-gray-200">
                                <th class="ppv2-pb-3 ppv2-text-left ppv2-w-10">
                                    <!-- Footer -->
                                    <div
                                        class="ppv2-mt-6 ppv2-flex ppv2-flex-col sm:ppv2-flex-row sm:ppv2-items-center sm:ppv2-justify-between ppv2-border-t ppv2-pt-4"
                                        :style="{ gap: normalizeSize(16, true) }"
                                    >
                                        <div class="ppv2-flex ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                                            <span class="ppv2-text-sm ppv2-text-gray-700" v-if="selectedInvoices.length > 0">
                                                <span class="poppinsMedium">{{ selectedInvoices.length }}</span>
                                                {{ $t('invoices.selected_elements') }}
                                            </span>
                                            <button
                                                v-if="selectedInvoices.length > 0"
                                                class="ppv2-inline-flex ppv2-items-center ppv2-gap-1 ppv2-text-sm ppv2-text-purple-600 hover:ppv2-text-purple-700 poppinsMedium ppv2-transition-colors"
                                            >
                                                <svg
                                                    class="ppv2-h-4 ppv2-w-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 17 17"
                                                >
                                                    <path
                                                        stroke-linecap="round"
                                                        stroke-linejoin="round"
                                                        stroke-width="2"
                                                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                                    />
                                                </svg>
                                                {{ $t('invoices.export') }}
                                            </button>
                                        </div>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ConfirmationDialog from '../Shared/ConfirmationDialog.vue'
import Search from '../V3/Experience/components/Search.vue'
import ExportSelect from '../Shared/ExportSelect.vue'
import BaseCheckbox from '../Shared/BaseCheckbox.vue'
import { FilePen, ChevronDown, FileText, CalendarClock, LayoutGrid } from 'lucide-vue'
import normalizeSize from '../../utils/normalizeSize'
import Header from '../Shared/Header.vue'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'InvoicesPageV2',
    components: {
        ConfirmationDialog,
        Search,
        ExportSelect,
        BaseCheckbox,
        FilePen,
        ChevronDown,
        FileText,
        CalendarClock,
        LayoutGrid,
        Header,
    },
    data() {
        return {
            filters: [
                { id: 'all', label: this.$t('invoices.status.all'), count: 0 },
                { id: 'paid', label: this.$t('invoices.status.paid'), count: 0 },
                { id: 'partial', label: this.$t('invoices.status.partial'), count: 0 },
                { id: 'due', label: this.$t('invoices.status.due'), count: 0 },
            ],

            selectedInvoices: [],
            activeFilter: 'all',
            showColumnSelector: false,
            showAdvancedFilters: false,
            showClearFiltersDialog: false,
            referenceData: null,
            loading: false,
            error: null,
            dateRange: {
                start: new Date().toISOString().split('T')[0],
                end: new Date().toISOString().split('T')[0],
            },
            searchQuery: '',
            exportOptions: [
                { value: 'csv', label: this.$t('common.csv') || 'CSV' },
                { value: 'xls', label: this.$t('common.excel') || 'Excel' },
            ],
        }
    },
    mounted() {
        this.loadReferenceData()
        this.loadStatusCounts()
        document.addEventListener('click', this.handleClickOutsideColumns)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutsideColumns)
    },
    watch: {
        'dateRange.start'() {
            this.loadStatusCounts()
        },
        'dateRange.end'() {
            this.loadStatusCounts()
        },
        activeFilter() {
            console.log('🏷️ Filtre de statut changé:', this.activeFilter)
            // Recharger le DataTable quand le filtre de statut change
            this.$nextTick(() => {
                if (this.$refs.invoiceDataTable) {
                    this.$refs.invoiceDataTable.reload()
                }
            })
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        getStatusColor(status) {
            const colors = {
                draft: 'ppv2-bg-yellow-400 ppv2-text-gray-800',
                sent: 'ppv2-bg-gray-400 ppv2-text-white',
                paid: 'ppv2-bg-emerald-500 ppv2-text-white',
                due: 'ppv2-bg-red-500 ppv2-text-white',
            }
            return colors[status] || 'ppv2-bg-gray-400 ppv2-text-white'
        },
        toggleInvoice(id) {
            const index = this.selectedInvoices.indexOf(id)
            if (index > -1) {
                this.selectedInvoices.splice(index, 1)
            } else {
                this.selectedInvoices.push(id)
            }
        },
        toggleAll() {
            if (this.selectedInvoices.length === this.invoices.length) {
                this.selectedInvoices = []
            } else {
                this.selectedInvoices = this.invoices.map((inv) => inv.id)
            }
        },
        async loadReferenceData() {
            try {
                this.loading = true
                const response = await this.axios.get('/back-office/invoices-v2/filter-data')
                // Extraction sécurisée des données (gestion axios object vs body direct)
                const body = response.data || response
                this.referenceData = body.data || body
                console.log('📊 Données de référence chargées:', this.referenceData)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.error = this.$t('invoices.errors.load_reference')
            } finally {
                this.loading = false
            }
        },
        async loadStatusCounts() {
            try {
                const filters = this.referenceData && this.referenceData.userFilters ? this.referenceData.userFilters.filters : {}

                const response = await this.axios.get('/back-office/invoices-v2/status-counts', {
                    params: {
                        startDate: this.dateRange.start,
                        endDate: this.dateRange.end,
                        search: this.searchQuery,
                        filters: filters,
                    },
                })
                // Gérer les différents formats de réponse
                const data = response.data || response
                this.filters = data.filters
                console.log('📊 Compteurs de statut chargés:', this.filters)
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleSearch(query) {
            this.searchQuery = query
            this.loadStatusCounts()
        },
        handleDateChange(dateRange) {
            // Les stats se rechargent automatiquement grâce au watch dans InvoiceStatsCards
        },
        isColumnSelected(columnId) {
            if (!this.referenceData?.userFilters?.selected_columns) return false
            return this.referenceData.userFilters.selected_columns.some((col) => col.column_id === columnId)
        },
        async toggleColumn(column) {
            if (!this.referenceData?.userFilters?.selected_columns) return

            const index = this.referenceData.userFilters.selected_columns.findIndex((col) => col.column_id === column.id)

            let newColumns
            if (index > -1) {
                // Retirer la colonne
                newColumns = this.referenceData.userFilters.selected_columns.filter((col) => col.column_id !== column.id)
            } else {
                // Ajouter la colonne
                newColumns = [
                    ...this.referenceData.userFilters.selected_columns,
                    {
                        column_id: column.id,
                        data: column.data,
                        name: column.name,
                        class: column.class,
                        title: column.title,
                    },
                ]
            }

            // Sauvegarder immédiatement
            await this.saveColumnsToBackend(newColumns)
        },
        async saveColumnsToBackend(columns) {
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')
                if (!csrfToken) {
                    console.error('CSRF token meta tag not found')
                    return
                }

                const response = await fetch('/back-office/invoices-v2/save-columns', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken.content,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({ columns }),
                })

                const data = await response.json()

                if (response.ok && data.success) {
                    this.referenceData.userFilters.selected_columns = data.columns

                    // Recharger le DataTable
                    this.$nextTick(() => {
                        if (this.$refs.invoiceDataTable) {
                            this.$refs.invoiceDataTable.reload()
                        }
                    })
                } else {
                    console.error('Erreur sauvegarde colonnes:', data)
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleClickOutsideColumns(event) {
            if (this.$refs.columnsDropdown && !this.$refs.columnsDropdown.contains(event.target)) {
                this.showColumnSelector = false
            }
        },
        updateFilters(newFilters) {
            console.log('🔍 Filtres mis à jour:', newFilters)
            // Mettre à jour les filtres dans referenceData
            if (this.referenceData && this.referenceData.userFilters) {
                this.referenceData.userFilters.filters = newFilters
            }
            // Recharger le DataTable
            this.$nextTick(() => {
                if (this.$refs.invoiceDataTable) {
                    this.$refs.invoiceDataTable.reload()
                }
                // Recharger les compteurs de statut
                this.loadStatusCounts()
                // Recharger les statistiques
                if (this.$refs.statsCards) {
                    this.$refs.statsCards.loadStats()
                }
            })
        },
        removeFilter(filterKey) {
            if (this.referenceData && this.referenceData.userFilters && this.referenceData.userFilters.filters) {
                // Supprimer le filtre
                const updatedFilters = { ...this.referenceData.userFilters.filters }
                delete updatedFilters[filterKey]

                // Sauvegarder
                this.saveFiltersToBackend(updatedFilters)

                // Recharger les compteurs de statut et les stats
                this.$nextTick(() => {
                    this.loadStatusCounts()
                    if (this.$refs.statsCards) {
                        this.$refs.statsCards.loadStats()
                    }
                })
            }
        },
        clearAllFilters() {
            // Afficher le dialogue de confirmation
            this.showClearFiltersDialog = true
        },
        async confirmClearAllFilters() {
            // Réinitialiser tous les filtres
            const emptyFilters = {}
            await this.saveFiltersToBackend(emptyFilters)
            this.showClearFiltersDialog = false

            // Recharger les compteurs de statut et les stats
            this.$nextTick(() => {
                this.loadStatusCounts()
                if (this.$refs.statsCards) {
                    this.$refs.statsCards.loadStats()
                }
            })
        },
        cancelClearFilters() {
            this.showClearFiltersDialog = false
        },
        async saveFiltersToBackend(filters) {
            try {
                const csrfToken = document.querySelector('meta[name="csrf-token"]')
                if (!csrfToken) {
                    console.error('CSRF token meta tag not found')
                    return
                }

                const response = await fetch('/back-office/invoices-v2/save-filters', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken.content,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({ filters }),
                })

                const data = await response.json()

                if (response.ok && data.success && this.referenceData) {
                    this.referenceData.userFilters.filters = data.filters

                    // Recharger le DataTable
                    this.$nextTick(() => {
                        if (this.$refs.invoiceDataTable) {
                            this.$refs.invoiceDataTable.reload()
                        }
                    })
                } else {
                    // Erreur
                    let errorMessage = this.$t('invoices.errors.update_filters')

                    if (response.status === 422 && data.errors) {
                        // Erreurs de validation Laravel - afficher tous les messages
                        const errors = Object.values(data.errors).flat()
                        errorMessage = errors.join('\n')
                    } else if (data.message) {
                        errorMessage = data.message
                    }

                    if (this.$toast) {
                        this.$toast.error(errorMessage)
                    } else {
                        alert(errorMessage)
                    }
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            }
        },
        handleSearchUpdate(searchValue) {
            console.log('🔍 Recherche:', searchValue)
            this.searchQuery = searchValue
            this.loadStatusCounts()
            // Trigger search in DataTable
            if (this.$refs.invoiceDataTable) {
                this.$refs.invoiceDataTable.handleSearchUpdate(searchValue)
            }
        },
        async handleExport(format) {
            console.log(`📤 Export ${format} demandé`)
            // Delegate to DataTable component
            if (this.$refs.invoiceDataTable) {
                await this.$refs.invoiceDataTable.exportData(format)
            }
        },
        getFilterPillLabel(key, value) {
            const labels = {
                has_refund: this.$t('invoices.filters.refund'),
                voucher_id: this.$t('invoices.filters.voucher'),
                promo_code: this.$t('invoices.filters.promo_code'),
                payment_method_id: this.$t('invoices.filters.payment_method'),
                prestations: this.$t('invoices.filters.activities'),
                products: this.$t('invoices.filters.products'),
                packages: this.$t('invoices.filters.packages'),
                series: this.$t('invoices.filters.subscriptions'),
                staff_id: this.$t('invoices.filters.employee'),
                status_id: this.$t('invoices.filters.status'),
                total_greater_than: this.$t('invoices.filters.min_amount'),
                total_lesser_than: this.$t('invoices.filters.max_amount'),
            }

            let displayValue = value

            // Formater la valeur selon le type
            if (Array.isArray(value)) {
                displayValue = `${value.length} ${this.$t('invoices.selected')}`
            } else if (typeof value === 'boolean') {
                displayValue = value ? this.$t('invoices.yes') : this.$t('invoices.no')
            } else if (key === 'voucher_id' && this.referenceData) {
                const voucher = this.referenceData.vouchers.find((v) => v.id === value)
                displayValue = voucher ? voucher.code : value
            } else if (key === 'payment_method_id' && this.referenceData) {
                const method = this.referenceData.paymentMethods.find((m) => m.id === value)
                displayValue = method ? method.display_name : value
            } else if (key === 'staff_id' && this.referenceData) {
                const staff = this.referenceData.staffs.find((s) => s.id === value)
                displayValue = staff ? staff.full_name : value
            } else if (key === 'status_id' && this.referenceData) {
                const status = this.referenceData.orderStatuses.find((s) => s.id === value)
                displayValue = status ? status.name : value
            }

            return {
                label: labels[key] || key,
                value: displayValue,
            }
        },
    },
    computed: {
        activeFiltersCount() {
            if (!this.referenceData || !this.referenceData.userFilters || !this.referenceData.userFilters.filters) {
                return 0
            }
            const filters = this.referenceData.userFilters.filters
            let count = 0
            Object.keys(filters).forEach((key) => {
                const value = filters[key]
                if (value !== null && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
                    count++
                }
            })
            return count
        },
        activeFiltersPills() {
            if (!this.referenceData || !this.referenceData.userFilters || !this.referenceData.userFilters.filters) {
                return {}
            }
            const filters = this.referenceData.userFilters.filters
            const pills = {}

            // Mapper chaque filtre actif vers un label lisible
            Object.keys(filters).forEach((key) => {
                const value = filters[key]
                if (value !== null && value !== '' && (Array.isArray(value) ? value.length > 0 : true)) {
                    pills[key] = this.getFilterPillLabel(key, value)
                }
            })

            return pills
        },
    },
}
</script>

<style scoped lang="scss">
.menuDropdown {
    @apply ppv2-bg-gray-900 ppv2-bg-opacity-80 ppv2-text-gray-400;
    margin-top: 10px;
    z-index: 9;
}

.dropdownItem:hover {
    @apply ppv2-bg-indigo-900 ppv2-text-white;
}

.dropdownItem:hover * {
    @apply ppv2-text-white;
}

.dark {
    .menuDropdown {
        @apply ppv2-bg-gray-800 ppv2-bg-opacity-90;
    }
}
</style>
